import { gameObjectTypes, parseCreatureLooks, parseNpcLooks, type CreatureLook, type GameObjectTypes, type NpcLook } from "odyssey-shared";
import { fetchNamed, type LoadedWorld } from "./world-loader.ts";

/**
 * The tables a loaded world version carries beside its chunks for the game page: the object types from its contract
 * (ADR-058) and the creature look table (ADR-082 decision 5), each read by the rule its manifest sets (P1.4-T14).
 */

/**
 * Where a version whose manifest names no contract keeps one, if it has one.
 */
const LEGACY_CONTRACT = "contract.json";

/**
 * The creature look table's path in a version directory (ADR-082 decision 5).
 */
const CREATURE_LOOKS = "creature-looks.json";

/**
 * The NPC look table's path in a version directory (ADR-136 decision 2.4).
 */
const NPC_LOOKS = "npc-looks.json";

/**
 * The object types a loaded world version runs with (ADR-058), from the contract in its version directory, which
 * the map server running the same version reads too, so the verb the game offers is the transition the map server
 * applies. A manifest that names its contract (P1.4-T14) says where it is, and one it names that answers 404 is an
 * error. A manifest naming none was written before files were named: its contract, if any, is `contract.json`, and a
 * version published before contracts has none (404) and runs with the built-in types. Any other failure is an error,
 * since a guessed table would offer verbs the map server refuses.
 *
 * @param world The loaded world.
 * @param fetcher The fetch to use.
 * @returns The object types and where they came from.
 * @throws Error naming the URL and status when the file answers with anything but success or a 404 the manifest
 *   allows, naming the URL when no answer came, or when the contract cannot be run with (see `gameObjectTypes`).
 */
export async function loadGameObjectTypes(world: LoadedWorld, fetcher: typeof fetch = fetch): Promise<GameObjectTypes> {
  const named = world.manifest.contract;
  const url = `${world.base}/worlds/${world.manifest.worldId}/${world.version}/${named ?? LEGACY_CONTRACT}`;
  const response = await fetchNamed(url, {}, fetcher);
  if (response.status === 404) {
    if (named !== undefined) {
      throw new Error(`${url}, which the manifest names, responded 404`);
    }
    return gameObjectTypes(null);
  }
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return gameObjectTypes(await response.text());
}

/**
 * The creature look table of a loaded world version (ADR-082 decision 5), from the `creature-looks.json` in its version
 * directory: a creature's `appearance` indexes it on the page as in the map server running the same version, which the
 * `welcome`'s world version guarantees. A version whose world places no spawn has none and draws no creature. A manifest
 * that names its files (P1.4-T14) says which: a table it does not name is not fetched, and one it names that answers
 * 404 is an error. For a manifest naming none, a 404 means no table.
 *
 * @param world The loaded world.
 * @param fetcher The fetch to use.
 * @returns The looks, by appearance.
 * @throws Error naming the URL and status when the file answers with anything but success or a 404 the manifest
 *   allows, naming the URL when no answer came, or when the table is malformed.
 */
export async function loadCreatureLooks(world: LoadedWorld, fetcher: typeof fetch = fetch): Promise<CreatureLook[]> {
  const namesFiles = world.manifest.contract !== undefined;
  const named = (world.manifest.files ?? []).includes(CREATURE_LOOKS);
  if (namesFiles && !named) {
    return [];
  }
  const url = `${world.base}/worlds/${world.manifest.worldId}/${world.version}/${CREATURE_LOOKS}`;
  const response = await fetchNamed(url, {}, fetcher);
  if (response.status === 404) {
    if (named) {
      throw new Error(`${url}, which the manifest names, responded 404`);
    }
    return [];
  }
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return parseCreatureLooks(await response.json());
}

/**
 * The NPC look table of a loaded world version (ADR-136 decision 2.4), from the `npc-looks.json` in its version
 * directory: an NPC's `appearance` indexes it as a creature's indexes `creature-looks.json`. The compile writes the
 * table only for a world that places an NPC and names it in the manifest's `files`, so a manifest that names its files
 * and not this one has none; one it names that answers 404 is an error. For a manifest naming none, a 404 means none.
 *
 * @param world The loaded world.
 * @param fetcher The fetch to use.
 * @returns The looks, by appearance.
 * @throws Error naming the URL and status when the file answers with anything but success or a 404 the manifest
 *   allows, naming the URL when no answer came, or when the table is malformed.
 */
export async function loadNpcLooks(world: LoadedWorld, fetcher: typeof fetch = fetch): Promise<NpcLook[]> {
  const namesFiles = world.manifest.contract !== undefined;
  const named = (world.manifest.files ?? []).includes(NPC_LOOKS);
  if (namesFiles && !named) {
    return [];
  }
  const url = `${world.base}/worlds/${world.manifest.worldId}/${world.version}/${NPC_LOOKS}`;
  const response = await fetchNamed(url, {}, fetcher);
  if (response.status === 404) {
    if (named) {
      throw new Error(`${url}, which the manifest names, responded 404`);
    }
    return [];
  }
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return parseNpcLooks(await response.json());
}
